import api from '../../api';
import { random } from '../../utils/strings';
import { dom } from '../../utils/dom';
import { constants } from '../../utils/constants';
import { roundToDecimal } from '../../utils/numbers';
const isEqual = require('lodash/isEqual');
const cloneDeep = require('lodash/cloneDeep');
const axios = require('axios');
import { loadStripe } from '@stripe/stripe-js/pure';

const DEFAULT_PKG = {
    sequence: [],
    token: '',
    alt_region_code: '',
    is_public: false,
    device_token: null,
    language: 'en',
};

export default {
    namespaced: true,
    state: {
        pkg: cloneDeep(DEFAULT_PKG),
        history: [],
        payments: [],
        contactInfo: [],
        pinProtection: null,
        cancelTokens: [],
        linkData: null
    },
    getters: {
        pkg(state) {
            return state.pkg;
        },
        title(state, getters, rootState, rootGetters) {
            return state.pkg.title || rootGetters['form/form'](0).name;
        },
        sequence(state) {
            return state.pkg.sequence;
        },
        attachment: state => id => {
            return state.pkg.attachments[id];
        },
        history(state) {
            return state.history;
        },
        payments(state) {
            return state.payments.filter(payment => payment.is_complete);
        },
        getContactInfo(state) {
            return state.contactInfo;
        },
        isComplete(state) {
            return state.pkg.is_complete;
        },
        isPublic(state) {
            return state.pkg.is_public;
        },
        revisionRef(state, getters, rootState, rootGetters) {
            if (!state.pkg.id) return '';
            return rootGetters['org/org'].alt_region_code + state.pkg.id + state.pkg.revision.id;
        },
        accessToken(state) {
            return state.pkg.device_token || state.pkg.token;
        },
        altRegionCode(state) {
            return state.pkg.alt_region_code;
        },
        tokenType(state) {
            if (state.pkg.device_token) return 'device';
            return state.pkg.is_public ? 'public' : 'private';
        },
        buildLink: (state, getters, rootState, rootGetters) => (pkg, includeDevice = true) => {
            let token, type;
            if (includeDevice && pkg.device_token) {
                token = pkg.device_token;
                type = 'd';
            } else {
                token = pkg.token;
                type = pkg.is_public ? 'public' : 'f';
            }
            let link = `${rootGetters['org/url']}/${type}/${pkg.alt_region_code}/${token}`;
            if (pkg.language !== 'en') link += `?l=${pkg.language}`;
            return link;
        },
        link(state, getters) {
            return getters.buildLink(getters.pkg, true);
        },
        revisionRef(state, getters) {
            if (!state.pkg.id) return '';
            return getters.altRegionCode + state.pkg.id + state.pkg.revision.id;
        },
        pinProtection(state) {
            return state.pinProtection;
        },
        contactById: state => contactId => {
            return state.contactInfo.find(contact => contact.id === contactId) || {};
        },
        isDeviceToken(state) {
            return !!state.pkg.device_token;
        },
        getCancelTokens(state) {
            return state.cancelTokens;
        },
        amountPaid(state, getters) {
            return roundToDecimal(
                getters.payments.reduce((acc, payment) => acc + payment.amount, 0)
            , 2);
        },
        linkData(state) {
            return state.linkData;
        },
        submitRedirectUrl(state) {
            if (!state.linkData || !state.linkData.submit_redirect) return false;
            let url = state.linkData.submit_redirect;
            if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
            return url;
        },
        language(state) {
            return state.pkg.language;
        },
    },
    mutations: {
        setPkg(state, pkg) {
            dom.convertLegacyFormData({ pkg });
            state.pkg = pkg;
        },
        reset(state) {
            state.pkg = cloneDeep(DEFAULT_PKG);
            state.history = [];
        },
        updateAttachment(state, { id, key, value }) {
            state.pkg.attachments[id][key] = value;
        },
        sequence(state, sequence) {
            state.pkg.sequence = sequence;
        },
        attachments(state, attachments) {
            state.pkg.attachments = attachments;
        },
        setHistory(state, history) {
            state.history = history;
        },
        payments(state, payments) {
            state.payments = payments || [];
        },
        setContactInfo(state, contactInfo) {
            state.contactInfo = contactInfo;
        },
        accessToken(state, token) {
            state.pkg.token = token;
        },
        pinProtection(state, pinProtection) {
            state.pinProtection = pinProtection || null;
        },
        deviceToken(state, token) {
            state.pkg.device_token = token;
        },
        cancelTokenPush(state, source) {
            state.cancelTokens.push(source);
        },
        clearCancelTokens(state) {
            state.cancelTokens = [];
        },
        isArchived(state, isArchived) {
            state.pkg.is_archived = isArchived;
        },
        isComplete(state, isComplete) {
            state.pkg.is_complete = isComplete;
            state.pkg.status = 'Active';
        },
        status(state, status) {
            state.pkg.status = status;
        },
        linkData(state, linkData) {
            state.linkData = linkData || null;
        }
    },
    actions: {
        cancelPendingSearches({ getters, commit }) {
            getters.getCancelTokens.forEach(source => {
                source.cancel('reqcancel');
            });
            commit('clearCancelTokens');
        },
        async search({ dispatch, commit }, { limit, offset, terms, sort, filter, returns = '*', cancellable = false }) {
            // if 'cancellable', will cancel any pending requests on new request, throwing an error
            let opts = {};
            if (cancellable) {
                dispatch('cancelPendingSearches');
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();
                commit('cancelTokenPush', source);
                opts.cancelToken = source.token;
            }

            let res = await api.post('/packages/search', {
                limit,
                offset,
                terms,
                sort,
                filter,
                returns,
            }, opts);

            if (cancellable) commit('clearCancelTokens');

            return res;
        },
        async loadFromToken({ dispatch }, { token, altRegionCode, linkType, pin, paymentId }) {
            let query = null;
            if (paymentId) {
                query = { paymentId };
            } else if (pin) {
                query = { pin };
            }
            let data = await api.get(`filler/${linkType}/${altRegionCode}/${token}`, query);
            dispatch('loadFromData', data);
        },
        async loadFromRef({ dispatch }, revisionRef) {
            let data = await api.get(`filler/ref/${revisionRef}`);
            dispatch('loadFromData', data);
        },
        async loadFromId({ dispatch }, pkgId) {
            let data = await api.get(`packages/${pkgId}`);
            dispatch('loadFromData', data);
        },
        loadFromData({ commit }, data) {
            commit('filler/isLoaded', false, { root: true });
            commit('setPkg', data.package);
            if (data.org) {
                commit('org/update', data.org, { root: true });
            }
            commit('pinProtection', data.pinProtection);
            commit('filler/recipient', data.recipient, { root: true });
            commit('payments', data.payments);
            commit('linkData', data.link);
        },
        async loadHistory({ commit, getters }) {
            let history = await api.get(`/packages/history/${getters.pkg.id}`);
            commit('setHistory', history);
        },
        async loadPayments({ commit, getters }) {
            let payments = await api.get(`payments/package/${getters.pkg.id}`);
            commit('payments', payments);
        },
        async reassignConfirm({ dispatch, rootGetters }, assigneeStr) {
            let assigneeDetails = rootGetters['boxes/parseBoxString'](assigneeStr);
            let body, type = 'confirm';
            if (!assigneeDetails) {
                body = 'Remove assignee?'
            } else if (assigneeDetails.id === rootGetters['user/user'].id) {
                body = 'Assign to yourself?';
            } else if (assigneeDetails.type === 'box') {
                let box = rootGetters['boxes/boxById'](assigneeDetails.id);
                body = `Assign to ${box.name}? Users with access to this box will be notified.<br><br>You may optionally add a message to these users:`;
                type = 'prompt';
            } else {
                body = `Assign to ${rootGetters['org/users'][assigneeDetails.id].name}? The user will be notified.<br><br>You may optionally add a message to this user:`;
                type = 'prompt';
            }
            let change = dispatch('dialog/set', {
                type,
                header: 'Change Assignee',
                body,
                okText: 'Ok',
                cancelText: 'Cancel',
            }, { root: true });

            return change;
        },
        sendFormDialog({ dispatch }, { publicLink = '', requiresReload = false } = {}) {
            dispatch('dialog/set', {
                componentName: 'send-form',
                dialogProps: {
                    showHeader: false,
                    showFooter: false,
                    showX: true,
                },
                bodyComponentProps: {
                    publicLink,
                    requiresReload,
                }
            }, { root: true });
        },
        async confirmNoAssignee({ rootGetters, commit, dispatch }) {
            // Warn user if sending without an assignee
            if (!rootGetters['office/assignedToUserId'] && !rootGetters['office/assignedToBoxId']) {
                const isSuper = rootGetters['auth/isSuperAdmin'];
                let msg, okText;
                if (isSuper) {
                    msg = 'Do you want to assign someone first?';
                    okText = 'Assign Someone';
                } else {
                    msg = 'Do you want to assign yourself instead?';
                    okText = 'Assign Me';
                }
                const reassign = await dispatch('dialog/set', {
                    type: 'confirm',
                    header: 'No Assigned User',
                    body: `There is no user currently assigned. ${msg}`,
                    okText,
                    cancelText: 'Continue Anyway'
                }, { root: true });
                if (reassign) {
                    if (isSuper) return false;
                    commit('office/assignedToUserId', rootGetters['user/user'].id, { root: true });
                }
            }
            return true;
        },
        async save({ getters, rootGetters, dispatch, commit }, saveType = 'draft') {
            // Save types are: "draft", "send", "submit", "save"
            let pkg = getters.pkg;
            if (saveType === 'submit' || rootGetters['filler/hasUnsavedChanges']) {
                // Save a new package revision
                let formData = rootGetters['filler/formData'];
                let req = {
                    saveType,
                    formData,
                    attachments: pkg.attachments,
                    revisionId: pkg.revision.id,
                    sequence: pkg.sequence,
                    sendingTo: rootGetters['office/sendingTo'],
                    sendingToContactId: rootGetters['office/sendingToContactId'],
                    sendingToUserId: rootGetters['office/sendingToUserId'],
                    sendingToBoxId: rootGetters['office/sendingToBoxId'],
                    title: rootGetters['office/title'] || pkg.title,
                    assignedToUserId: rootGetters['office/assignedToUserId'],
                    assignedToBoxId: rootGetters['office/assignedToBoxId'],
                };

                if (saveType === 'send' && rootGetters['office/sendEmail']) {
                    req.email = rootGetters['office/email'];
                }

                let res = await api.put(`packages/${pkg.id}`, req);

                if (res.newToken) commit('accessToken', res.newToken);
                if (saveType === 'submit') {
                    commit('status', 'Active');
                } else if (saveType === 'send') {
                    commit('status', 'Pending');
                }
                commit('pinProtection', res.pinProtection);

                if (saveType === 'send' || res.paymentSession) {
                    commit('filler/hasUnsavedChanges', false, { root: true });
                } else {
                    await dispatch('filler/load', { id: res.packageId }, { root: true });
                }
                commit('office/init', null, { root: true });

                // Go to payment processor website if necessary
                if (res.paymentSession) {
                    await dispatch('goToPayment', res.paymentSession);
                }

                return {
                    ...res,
                    action: 'save'
                };
            } else if (saveType === 'send') {
                // Get a new link if necessary or send email (without saving a revision)
                let res = await api.post(`packages/send/${pkg.id}`, {
                    sendingTo: rootGetters['office/sendingTo'],
                    sendingToContactId: rootGetters['office/sendingToContactId'],
                    sendingToUserId: rootGetters['office/sendingToUserId'],
                    sendingToBoxId: rootGetters['office/sendingToBoxId'],
                    title: rootGetters['office/title'] || pkg.title,
                    assignedToUserId: rootGetters['office/assignedToUserId'],
                    assignedToBoxId: rootGetters['office/assignedToBoxId'],
                    email: rootGetters['office/sendEmail'] ? rootGetters['office/email'] : null
                });

                if (res.token) commit('accessToken', res.token);
                commit('status', 'Pending');
                commit('pinProtection', res.pinProtection);
                commit('office/init', null, { root: true });
                return {
                    ...res,
                    action: 'send'
                };
            } else if (rootGetters['office/hasUnsavedChanges']) {
                // Update the current revision's meta data
                let res = await api.post(`packages/update/${pkg.id}`, {
                    sendingTo: rootGetters['office/sendingTo'],
                    sendingToContactId: rootGetters['office/sendingToContactId'],
                    sendingToUserId: rootGetters['office/sendingToUserId'],
                    sendingToBoxId: rootGetters['office/sendingToBoxId'],
                    title: rootGetters['office/title'] || pkg.title,
                    assignedToUserId: rootGetters['office/assignedToUserId'],
                    assignedToBoxId: rootGetters['office/assignedToBoxId'],
                });
                commit('office/init', null, { root: true });
                return {
                    ...res,
                    action: 'update'
                };
            }
            return false;
        },
        async fillerSave({ state, commit, getters, rootGetters, dispatch }, data) {
            // Don't need to save a revision if we're not submitting, the form wasn't changed,
            // and the form isn't public (if public we still need to save a private copy so we
            // can email the link to finish later).
            if (
                !data.submit &&
                !rootGetters['filler/hasUnsavedChanges'] &&
                getters.tokenType !== 'public'
            ) {
                return api.put(`${rootGetters['filler/baseEndpoint']}/endSession`, data);
            }
            
            data.attachments = state.pkg.attachments;
            data.revisionId = state.pkg.revision.id;
            let saveData = await api.put(rootGetters['filler/baseEndpoint'], data);

            if (getters.isDeviceToken) {
                commit('filler/hasUnsavedChanges', false, { root: true });
                return;
            }

            // verify the data has saved without any data loss
            let savedInstances = saveData.package.form_instances;
            for (let i = 0; i < state.pkg.sequence.length; i++) {
                let seq = state.pkg.sequence[i];
                if (seq.type !== 'form') continue;
                let thisData = cloneDeep(data.formData[seq.id]);
                let newId = saveData.package.sequence[i].id;
                let thatData;
                if (savedInstances[newId]) {
                    thatData = savedInstances[newId].form_data;

                    // Ignore "time" property in formData, since it will be set by server
                    for (let fid in thisData) {
                        if (thisData[fid].time !== undefined) {
                            thisData[fid].time = thatData[fid].time;
                        }
                    }
                }
                if (!isEqual(thisData, thatData)) {
                    throw new Error(
                        rootGetters['lang/tr'].saveFormError(rootGetters['org/org'].name)
                    );
                }
            }

            commit('filler/hasUnsavedChanges', false, { root: true });

            // Go to payment processor website if necessary
            if (saveData.paymentSession) {
                return await dispatch('goToPayment', saveData.paymentSession);
            }

            // Go to submission redirect URL if set
            if (data.submit && getters.submitRedirectUrl) {
                window.location.href = getters.submitRedirectUrl;
                return await new Promise(() => {});
            }

            dispatch('loadFromData', saveData);

            if (data.submit) {
                commit('filler/isLoaded', true, { root: true });
            } else {
                dispatch('filler/load', {}, { root: true });
            }
        },
        async print({ getters, dispatch }, opts = {}) {
            if (opts.packageId || opts.revisionRef || getters.revisionRef) {
                dispatch('openPDF', { ...opts, print: true });
            } else {
                dispatch('form/print', null, { root: true });
            }
        },
        download({ getters, dispatch }, opts = {}) {
            if (opts.packageId || opts.revisionRef || getters.revisionRef) {
                dispatch('openPDF', opts);
            } else {
                dispatch('form/download', null, { root: true });
            }
        },
        async openPDF({ getters, rootGetters, dispatch }, options) {
            let revisionRef;
            if (options.revisionRef) {
                revisionRef = options.revisionRef;
            } else if (options.packageId) {
                if (!options.revisionId) throw new Error('Revision ID must be specified when opening PDF by package ID');
                revisionRef = rootGetters['org/org'].alt_region_code + options.packageId + options.revisionId;
            } else {
                revisionRef = getters.revisionRef;
            }

            // Circumvent the browser popup blocker by synchronously creating a new tab
            let tab = window.open('', 'paperwork_pdf' + random(4));
            if (!tab) {
                dispatch('notification/set', {
                    type: 'error',
                    message: rootGetters['lang/tr'].pdfTabBlocked(options.print),
                });
                return;
            }

            // If we are opening the currently loaded revision's existing PDF, no need to call API
            if (
                revisionRef === getters.revisionRef &&
                getters.pkg.revision.pdf_url &&
                !rootGetters['filler/hasUnsavedChanges']
            ) {
                tab.location.href = getters.pkg.revision.pdf_url;
                return;
            }

            // Ensure the API is healthy before we request a PDF link for printing
            if (options.print) {
                try {
                    await api.get('d/status', null, { timeout: 2000 });
                } catch (err) {
                    console.error(err)
                    if (!err.details) {
                        // Fall back to printing the current page
                        tab.close();
                        dispatch('notification/clear', null, { root: true });
                        setTimeout(() => window.print());
                        return;
                    }
                }
            }

            let endpoint = `filler/ref/${revisionRef}/pdf`;
            let reqData = {};
            let res;
            try {
                if (rootGetters['filler/hasUnsavedChanges']) {
                    // Post the current (unsaved) form data to be used in the PDF
                    reqData.formData = rootGetters['filler/formData'];
                    if (rootGetters['filler/isOffice']) {
                        reqData.forms = rootGetters['form/forms'];
                    }
                    res = await api.post(endpoint, reqData);
                } else {
                    // Form data is saved server-side, so we can just GET a PDF URL
                    res = await api.get(endpoint, reqData);
                }
            } catch (err) {
                tab.close();
                throw err;
            }
            if (tab.closed) return;
            if (res.pending) {
                let query = api.queryString({ ref: revisionRef, token: res.token });
                tab.location.href = `${window.location.origin}/pdf${query}`;
            } else {
                tab.location.href = res.url;
            }
        },
        async updateMultiple({ }, { packageIds, updates }) {
            await api.post('packages/update', { packageIds, ...updates });
        },
        async update({ }, { updates, pkgId }) {
            await api.post(`packages/update/${pkgId}`, updates);
        },
        async deleteDrafts({ dispatch }, { packageIds, skipConfirm = false }) {
            let deleteString = packageIds.length > 1 ? `${packageIds.length} Drafts` : 'Draft';
            let doChange = skipConfirm || await dispatch('dialog/set', {
                type: 'confirm',
                header: `Delete ${deleteString}?`,
                critical: true,
                body: `This cannot be undone.`,
                okText: 'Delete Forever'
            }, { root: true });

            if (!doChange) return false;

            await api.post('packages/delete', { packageIds });

            return true;
        },
        async archivePkgs({ dispatch }, { packageIds, archive = true, skipConfirm = false }) {
            let action = archive ? 'Archive' : 'Unarchive';
            let archiveString = packageIds.length > 1 ? `${packageIds.length} Form Packages` : 'Form Package';
            let doChange = skipConfirm || await dispatch('dialog/set', {
                type: 'confirm',
                header: `${action} ${archiveString}?`,
                critical: true,
                body: 'This will affect all users.',
                okText: action,
            }, { root: true });

            if (!doChange) return false;

            await api.post(`packages/update`, { packageIds, isArchived: archive });

            return true;
        },
        async deletePackages({ dispatch, commit }, pkgIds) {
            let multi = pkgIds.length > 1;
            let deleteString = multi ? `${pkgIds.length} Form Packages` : 'Form Package';
            let doChange = await dispatch('dialog/set', {
                type: 'confirm',
                header: `Delete ${deleteString}`,
                critical: true,
                body: `
                    Are you sure you want to permanently delete ${multi ? '' : 'this'} ${deleteString.toLowerCase()}?
                    <br><br>The form content, attachments, and PDF will not be recoverable.
                `,
                okText: 'Delete Forever',
            }, { root: true });

            if (!doChange) return false;

            commit('base/setLoading', { isLoading: true, fullScreen: false }, { root: true });
            commit('base/loadingMessage', `Deleting Package${multi ? 's' : ''}`, { root: true });
            await api.post('packages/delete', { packageIds: pkgIds });

            return true;
        },
        async retract({ getters, commit }) {
            let res = await api.post(`packages/retract/${getters.pkg.id}`);
            commit('status', res.status);
        },
        async loadContactInfo({ rootGetters, commit }) {
            let contactIds = rootGetters['form/forms'].reduce((acc, form) => {
                let formContactIds = form.data.dataElements.reduce((acc, dataElement) => {
                    if (dataElement.type === 'contact') {
                        let fieldData = rootGetters['filler/fieldData'](form.id, dataElement.featureId);
                        if (fieldData.selectedDataElementDataId) {
                            acc.push(fieldData.selectedDataElementDataId);
                        }
                    }
                    return acc;
                }, []);

                acc = acc.concat(formContactIds);
                return acc;
            }, []);

            let uniqueContactIds = [...new Set(contactIds)];
            let promises = uniqueContactIds.map(async id => {
                return await api.get(`dataElements/data/${id}`);
            });

            let contactInfo = await Promise.all(promises);
            contactInfo = contactInfo.sort((a, b) => {
                return a.lname + a.fname > b.lname + b.fname ? 1 : -1;
            });

            commit('setContactInfo', contactInfo);
        },
        async getDeviceLink({ getters, rootGetters, commit }) {
            if (!getters.pkg.device_token) {
                let res = await api.post(`packages/deviceToken/${getters.pkg.id}`);
                commit('deviceToken', res.token);
            }
            return `${rootGetters['org/url']}/d/${getters.altRegionCode}/${getters.pkg.device_token}`;
        },
        async deleteDeviceLink({ getters }) {
            await api.delete(`packages/deviceToken/${getters.pkg.id}`);
        },
        async validateFieldValues({ getters, rootGetters, dispatch }) {
            let invalidIds = await api.post(`packages/validate/${getters.pkg.id}`, { formData: rootGetters['filler/formData'] });
            if (invalidIds.length) {
                let message = `${invalidIds.length === 1 ? "This field has" : `${invalidIds.length} fields have`} incorrect formatting.`;
                dispatch('filler/markInvalidInputs', { ids: invalidIds, message }, { root: true });
                return false;
            }
            return true;
        },
        async goToPayment({ dispatch, rootGetters }, session) {
            try {
                if (session.processor === constants.PAYMENT_PROCESSORS.MONERIS) {
                    // Redirect to the Moneris hosted payment page with a POST request
                    let url = rootGetters['base/isDev']
                        ? 'https://esqa.moneris.com/HPPDP/index.php'
                        : 'https://www3.moneris.com/HPPDP/index.php';
                    dom.formPost(url, {
                        hpp_id: session.hppId,
                        hpp_preload: '',
                        ticket: session.ticket
                    });
                    return new Promise(() => {});
                } else if (session.processor === constants.PAYMENT_PROCESSORS.STRIPE) {
                    // Use the Stripe SDK to redirect to the checkout page
                    let stripe = await loadStripe(session.publishableKey);
                    let res = await stripe.redirectToCheckout({ sessionId: session.sessionId });
                    if (res.error) throw result.error;
                } else if (session.processor === constants.PAYMENT_PROCESSORS.PSN) {
                    // Redirect to the PSN One-Time Payment page with a POST
                    let url = rootGetters['base/isDev']
                        ? 'https://sandbox.paymentservicenetwork.com/psnautoauth.asp'
                        : 'https://www.paymentservicenetwork.com/psnautoauth.asp';
                    dom.formPost(url, {
                        AccountId: session.accountId,
                        PayerName: `${rootGetters['filler/submittedByFname']} ${rootGetters['filler/submittedByLname']}`,
                        Payment_Amt: rootGetters['filler/paymentTotal'],
                        Customer_Number: session.paymentRef,
                        security_code: 775,
                    });
                    return new Promise(() => {});
                }
            } catch (err) {
                console.error(err);
                dispatch('dialog/set', {
                    type: 'alert',
                    header: '',
                    critical: true,
                    body: `${rootGetters['lang/tr'].failRedirectToPayment}<br><br>${err.message}`
                }, { root: true });
            }
        }
    },
}
